import { useEffect, useRef, useState } from "react";
import { setShellNavigationVisible } from "./shell-chrome";

/**
 * The screen the convention opens on, once, inside the installed application.
 *
 * It waits. A teacher sees this on the day they install the application, which is the
 * evening before a convention rather than the morning of it, and it is the one screen
 * here that is worth looking at rather than reading. A screen that removes itself after a
 * second and a half decides how long that is worth; a button lets the person decide, and
 * it costs them one tap, once, ever.
 *
 * Three conditions, and all of them have to hold.
 *
 * It belongs to the native shell. In a browser this is a web page, and a page that opens
 * with a full-bleed photograph before showing anything is a splash screen on a website —
 * on a laptop it would be a portrait picture stretched across a landscape window. The
 * shell says who it is; a browser says nothing and gets nothing.
 *
 * It belongs to the first launch after installing, not to every launch. A teacher opens
 * this a dozen times between sessions and a second and a half each time is a toll. The
 * mark is kept on the device, so uninstalling and installing again shows it again, which
 * is what somebody would expect.
 *
 * And it belongs to the front door. Somebody arriving from a push notification or a
 * saved session link asked for a particular page, and a launch screen in front of it is a
 * delay rather than a welcome.
 *
 * It stands in front of the loading state rather than after it, so the application opens
 * on the gorge instead of on the word "Loading". The words arrive with the guide, which
 * is where every one of them comes from — the event's name, both halves of WVCEA's theme
 * and the dates. Only the button is written for the screen.
 *
 * The photograph is "Diamond Point Sunrise" by Gary Hartley, taken for the National Park
 * Service in the New River Gorge. It is public domain and needs no attribution; the
 * licence and the source URL are recorded in assets/Photography beside the original.
 */

/** The fade out. Must match the transition in the stylesheet. */
const FADE_MS = 700;

const SEEN_KEY = "wvcea:welcome-seen:v1";

export interface WelcomeEvent {
  name: string;
  themePrimary: string;
  themeSecondary: string;
  dateLine: string;
}

/**
 * The Worker marks native-shell HTML before the application runs. The old injected
 * window object remains accepted so an installed v1 shell and the current Worker agree.
 */
export interface NativeShell { platform: string }

export function nativeShell(scope: { wvceaShell?: unknown; document?: Document } = window as Window & { wvceaShell?: unknown }): NativeShell | null {
  const value = scope.wvceaShell;
  if (value && typeof value === "object") {
    const platform = (value as { platform?: unknown }).platform;
    if (typeof platform === "string" && platform.length > 0) return { platform };
  }
  const declared = scope.document?.documentElement.dataset.shell;
  return declared ? { platform: declared } : null;
}

export function welcomeAlreadySeen(storage: Pick<Storage, "getItem"> = localStorage): boolean {
  try {
    return storage.getItem(SEEN_KEY) !== null;
  } catch {
    // No storage means no way to remember, and a welcome that cannot be remembered
    // would come back on every launch. It stays away instead.
    return true;
  }
}

export function rememberWelcomeSeen(storage: Pick<Storage, "setItem"> = localStorage): void {
  try {
    storage.setItem(SEEN_KEY, new Date().toISOString());
  } catch { /* The screen has been shown. It simply will not be remembered. */ }
}

export function shouldShowWelcome({ pathname, inShell, alreadySeen }: {
  pathname: string;
  inShell: boolean;
  alreadySeen: boolean;
}): boolean {
  return inShell && !alreadySeen && pathname === "/";
}

export function WelcomeScreen({ event }: { event: WelcomeEvent | null }) {
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(false);
  const enter = useRef<HTMLButtonElement>(null);

  // The one thing on the screen a finger is meant to find, so it is also the one thing
  // a screen reader lands on.
  useEffect(() => { enter.current?.focus(); }, []);

  // The shell's bar has nothing to do over a screen that fills the display and offers one
  // way onward, and on the phone it sat across the button that takes you there. Put back
  // on the way out, and on unmount, so a shell is never left without its navigation.
  useEffect(() => {
    setShellNavigationVisible(false);
    return () => { setShellNavigationVisible(true); };
  }, []);

  // Marked on the way out rather than on the way in, so an application that falls over
  // while opening for the first time still gets to show this once.
  useEffect(() => {
    if (!leaving) return;
    rememberWelcomeSeen();
    setShellNavigationVisible(true);
    const end = window.setTimeout(() => setGone(true), FADE_MS);
    return () => window.clearTimeout(end);
  }, [leaving]);

  if (gone) return null;
  return (
    <div className={`welcome-screen${leaving ? " is-leaving" : ""}`} role="dialog" aria-modal="true" aria-label={event?.name ?? "Welcome"}>
      <picture>
        <source type="image/avif" srcSet="/welcome/diamond-point-sunrise-1320.avif 1320w, /welcome/diamond-point-sunrise-1440.avif 1440w" sizes="100vw" />
        <source type="image/webp" srcSet="/welcome/diamond-point-sunrise-1320.webp 1320w, /welcome/diamond-point-sunrise-1440.webp 1440w" sizes="100vw" />
        <img className="welcome-photograph" src="/welcome/diamond-point-sunrise-1320.webp" alt="" />
      </picture>
      <div className="welcome-scrim" />
      {event && (
        <div className="welcome-words">
          {/* The year comes off, because it is on the line underneath. "2026 WVCEA
              Educators' Convention" above "September 17–18, 2026" says it twice. */}
          <p className="welcome-title">{event.name.replace(/^\d{4}\s+/, "")}</p>
          {/* The first half of the theme only. Both halves is four lines of italic in
              the middle of a photograph, and the second half is on Event details. */}
          <p className="welcome-theme">{event.themePrimary}</p>
          <p className="welcome-date">{event.dateLine}</p>
        </div>
      )}
      {/* Present from the first frame, whether or not the guide has arrived. Somebody
          who taps before it lands gets the loading state, which is the truth. */}
      <button ref={enter} type="button" className="welcome-enter" onClick={() => setLeaving(true)}>Enter convention</button>
    </div>
  );
}
